// NewsFirebaseService.js - Version simplifiée sans gestion des mots-clés par thème
import { collection, doc, addDoc, getDocs, getDoc, deleteDoc, updateDoc, query, where, orderBy, limit, increment, documentId } from 'firebase/firestore';
import { db } from '../firebase';
import { getAuth } from 'firebase/auth';
import sourceService from '../CP-Sourcing/SourceService';
import { askGPT } from '../components/OpenAIService';

class NewsFirebaseService {
  constructor() {
    this.cache = new Map();
    this.semanticCache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
    this.semanticCacheTimeout = 30 * 60 * 1000; // 30 minutes
    
    // Configuration des thèmes (uniquement pour la découverte par thème)
    this.newsThemes = {
      'sports': {
        searchTerms: 'sport fitness exercice activité physique santé actualités',
        categories: ['sports', 'health', 'fitness']
      },
      'creativity': {
        searchTerms: 'créativité design art culture loisirs divertissement actualités',
        categories: ['arts', 'design', 'culture', 'entertainment']
      },
      'travel': {
        searchTerms: 'voyage tourisme destination vacances découverte actualités',
        categories: ['travel', 'tourism', 'lifestyle']
      },
      'finance': {
        searchTerms: 'finance bourse investissement banque économie crypto actualités',
        categories: ['finance', 'business', 'economy', 'investment']
      },
      'entrepreneurship': {
        searchTerms: 'startup entrepreneur business innovation financement entreprise actualités',
        categories: ['business', 'startup', 'innovation', 'economy']
      },
      'career': {
        searchTerms: 'carrière emploi travail recrutement formation professionnelle actualités',
        categories: ['career', 'employment', 'business', 'professional']
      },
      'health': {
        searchTerms: 'santé médecine bien-être nutrition soins prévention actualités',
        categories: ['health', 'medicine', 'wellness', 'science']
      },
      'education': {
        searchTerms: 'formation éducation apprentissage école université compétences actualités',
        categories: ['education', 'learning', 'academic', 'training']
      },
      'realestate': {
        searchTerms: 'immobilier logement propriété investissement habitat actualités',
        categories: ['realestate', 'property', 'investment', 'housing']
      },
      'personal': {
        searchTerms: 'développement personnel coaching psychologie motivation leadership actualités',
        categories: ['personal', 'psychology', 'coaching', 'self-improvement']
      }
    };
  }

  // ================================
  // MÉTHODES PRINCIPALES - ACCÈS DIRECT AUX CORRESPONDANCES
  // ================================

  async getNewsForObjective(objectiveId, userId, options = {}) {
    try {
      const { includeHistory = false, maxHistory = 3 } = options;
      
      console.log(`Récupération des actualités pour objectif: ${objectiveId}`);
      
      const latestResult = await this.getLatestObjectiveResults(objectiveId, userId);
      
      if (!latestResult) {
        console.log('Aucune correspondance trouvée pour cet objectif');
        return {
          current: [],
          history: [],
          hasData: false,
          message: 'Aucune analyse disponible pour cet objectif'
        };
      }

      const currentNews = await this.getNewsFromStoredIds(latestResult.selectedNewsIds || []);
      
      const result = {
        current: currentNews,
        synthesis: latestResult.synthesis || '',
        analysisMetadata: latestResult.analysisMetadata || {},
        lastAnalysis: latestResult.createdAt,
        hasData: currentNews.length > 0,
        message: `${currentNews.length} actualités pertinentes trouvées`
      };

      if (includeHistory) {
        const history = await this.getObjectiveResultsHistory(objectiveId, userId, maxHistory);
        result.history = history.slice(1);
      }

      console.log(`${currentNews.length} actualités récupérées pour l'objectif`);
      return result;
      
    } catch (error) {
      console.error('Erreur getNewsForObjective:', error);
      return {
        current: [],
        history: [],
        hasData: false,
        error: error.message
      };
    }
  }

  async refreshObjectiveAnalysis(objective, userId, options = {}) {
    try {
      const {
        maxNews = 50,
        language = 'fr',
        country = 'FR'
      } = options;

      console.log(`Rafraîchissement complet pour objectif: "${objective.title}"`);

      const result = await this.discoverAndAnalyzeForObjective(objective, userId, {
        maxNews,
        language,
        country,
        saveResults: true
      });

      console.log(`Analyse rafraîchie: ${result.analysis?.relevant || 0} actualités pertinentes`);
      
      return {
        success: result.success,
        discovered: result.enrichment?.discovered || 0,
        added: result.enrichment?.added || 0,
        analyzed: result.analysis?.relevant || 0,
        message: result.message || 'Analyse rafraîchie avec succès'
      };
      
    } catch (error) {
      console.error('Erreur refresh analyse:', error);
      throw error;
    }
  }

  async hasRecentAnalysis(objectiveId, userId, maxAgeHours = 24) {
    try {
      const latestResult = await this.getLatestObjectiveResults(objectiveId, userId);
      
      if (!latestResult) return false;

      const analysisDate = new Date(latestResult.createdAt);
      const now = new Date();
      const ageHours = (now - analysisDate) / (1000 * 60 * 60);

      return ageHours <= maxAgeHours;
      
    } catch (error) {
      console.error('Erreur vérification analyse récente:', error);
      return false;
    }
  }

  async getObjectiveSummary(objectiveId, userId) {
    try {
      const latestResult = await this.getLatestObjectiveResults(objectiveId, userId);
      
      if (!latestResult) {
        return {
          hasData: false,
          message: 'Aucune analyse disponible'
        };
      }

      const newsCount = latestResult.selectedNewsIds?.length || 0;
      const analysisDate = new Date(latestResult.createdAt);
      const ageHours = Math.round((new Date() - analysisDate) / (1000 * 60 * 60));

      return {
        hasData: newsCount > 0,
        newsCount: newsCount,
        analysisAge: ageHours,
        lastAnalysis: latestResult.createdAt,
        synthesis: latestResult.synthesis?.substring(0, 200) + (latestResult.synthesis?.length > 200 ? '...' : ''),
        avgScore: latestResult.analysisMetadata?.avgScore || 0,
        message: `${newsCount} actualités analysées il y a ${ageHours}h`
      };
      
    } catch (error) {
      console.error('Erreur résumé objectif:', error);
      return {
        hasData: false,
        error: error.message
      };
    }
  }

  // ================================
  // MÉTHODES DE PERSISTANCE DES CORRESPONDANCES
  // ================================

  async saveObjectiveNewsResults(objectiveData, selectedNewsIds, synthesis, analysisMetadata, userId) {
    try {
      console.log('Sauvegarde des résultats pour objectif:', objectiveData.title);
      
      const timestamp = new Date().toISOString();
      
      const resultDoc = {
        objectiveId: objectiveData.id,
        objectiveSnapshot: {
          title: objectiveData.title,
          snapshotDate: timestamp
        },
        selectedNewsIds: selectedNewsIds || [],
        synthesis: synthesis || '',
        analysisMetadata: {
          totalAnalyzed: analysisMetadata?.totalAnalyzed || 0,
          relevantFound: analysisMetadata?.relevantFound || 0,
          avgScore: analysisMetadata?.avgScore || 0,
          analysisTime: analysisMetadata?.analysisTime || 0,
          lastAnalysis: timestamp
        },
        createdAt: timestamp,
        createdBy: userId,
        status: 'active'
      };

      const docRef = await addDoc(collection(db, 'objective_news_results'), resultDoc);
      
      console.log('Résultats sauvegardés avec ID:', docRef.id);
      
      await this.archiveOldObjectiveResults(objectiveData.id, userId);
      
      return { ...resultDoc, id: docRef.id };
      
    } catch (error) {
      console.error('Erreur sauvegarde résultats objectif:', error);
      throw error;
    }
  }

  async getLatestObjectiveResults(objectiveId, userId) {
    try {
      console.log('Récupération dernier résultat pour objectif:', objectiveId);
      
      const q = query(
        collection(db, 'objective_news_results'),
        where('objectiveId', '==', objectiveId),
        where('createdBy', '==', userId),
        where('status', '==', 'active'),
        orderBy('createdAt', 'desc'),
        limit(1)
      );

      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        console.log('Aucun résultat trouvé pour cet objectif');
        return null;
      }

      const doc = querySnapshot.docs[0];
      const result = {
        id: doc.id,
        ...doc.data()
      };

      console.log('Dernier résultat trouvé:', {
        date: result.createdAt,
        articlesCount: result.selectedNewsIds?.length || 0,
        hasSynthesis: !!result.synthesis
      });

      return result;
      
    } catch (error) {
      console.error('Erreur récupération résultats objectif:', error);
      return null;
    }
  }

  async getNewsFromStoredIds(newsIds) {
    try {
      if (!newsIds || newsIds.length === 0) {
        return [];
      }

      console.log(`Récupération de ${newsIds.length} actualités stockées`);

      const batchSize = 10;
      const allNews = [];
      
      for (let i = 0; i < newsIds.length; i += batchSize) {
        const batch = newsIds.slice(i, i + batchSize);
        
        try {
          const q = query(
            collection(db, 'news'),
            where(documentId(), 'in', batch)
          );

          const snapshot = await getDocs(q);
          const batchNews = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          console.log(`Batch ${i}-${i+batch.length}: ${batchNews.length}/${batch.length} actualités trouvées`);
          allNews.push(...batchNews);
          
        } catch (batchError) {
          console.error('Erreur récupération batch:', batchError);
        }
      }

      const orderedNews = newsIds.map(id => 
        allNews.find(news => news.id === id)
      ).filter(Boolean);

      console.log(`${orderedNews.length}/${newsIds.length} actualités récupérées avec succès`);
      
      return orderedNews.map(this.enhanceNewsData);
      
    } catch (error) {
      console.error('Erreur récupération actualités par IDs:', error);
      return [];
    }
  }

  async archiveOldObjectiveResults(objectiveId, userId) {
    try {
      const q = query(
        collection(db, 'objective_news_results'),
        where('objectiveId', '==', objectiveId),
        where('createdBy', '==', userId),
        where('status', '==', 'active'),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);
      const results = snapshot.docs;

      if (results.length > 3) {
        const toArchive = results.slice(3);
        
        for (const docToArchive of toArchive) {
          await updateDoc(doc(db, 'objective_news_results', docToArchive.id), {
            status: 'archived',
            archivedAt: new Date().toISOString()
          });
        }
        
        console.log(`${toArchive.length} anciens résultats archivés pour objectif ${objectiveId}`);
      }
      
    } catch (error) {
      console.warn('Erreur archivage anciens résultats:', error);
    }
  }

  async getObjectiveResultsHistory(objectiveId, userId, limit = 5) {
    try {
      const q = query(
        collection(db, 'objective_news_results'),
        where('objectiveId', '==', objectiveId),
        where('createdBy', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(limit)
      );

      const snapshot = await getDocs(q);
      const history = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      return history;
      
    } catch (error) {
      console.error('Erreur récupération historique:', error);
      return [];
    }
  }

  async deleteObjectiveResult(resultId, userId) {
    try {
      const docRef = doc(db, 'objective_news_results', resultId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists() || docSnap.data().createdBy !== userId) {
        throw new Error('Résultat non trouvé ou accès non autorisé');
      }

      await deleteDoc(docRef);
      console.log('Résultat supprimé:', resultId);
      
    } catch (error) {
      console.error('Erreur suppression résultat:', error);
      throw error;
    }
  }

  // ================================
  // MÉTHODES DE DÉCOUVERTE ET ANALYSE
  // ================================

  async discoverAndAnalyzeForObjective(objective, userId, options = {}) {
    try {
      const {
        maxNews = 50,
        language = 'fr',
        country = 'FR',
        saveResults = true
      } = options;

      console.log(`Découverte + Analyse complète pour: "${objective.title}"`);

      // Utiliser directement le titre de l'objectif pour la recherche
      const enrichResult = await this.discoverAndAddNews(objective.title, userId, {
        maxNews,
        language,
        country
      });

      console.log(`Enrichissement: ${enrichResult.added} nouvelles actualités découvertes`);

      // Analyse sémantique sur les nouvelles actualités découvertes
      const relevantNews = enrichResult.news.filter(news => {
        const content = `${news.title} ${news.content}`.toLowerCase();
        const keywords = this.extractKeywordsFromObjective(objective);
        return keywords.some(keyword => content.includes(keyword.toLowerCase()));
      });

      if (saveResults && relevantNews.length > 0) {
        try {
          const selectedNewsIds = relevantNews.map(news => news.id);
          
          const basicSynthesis = await this.generateBasicSynthesis(relevantNews, objective);
          
          const analysisMetadata = {
            totalAnalyzed: enrichResult.discovered || 0,
            relevantFound: relevantNews.length,
            avgScore: relevantNews.length > 0 ? 7 : 0,
            analysisTime: Date.now(),
            lastAnalysis: new Date().toISOString()
          };
          
          await this.saveObjectiveNewsResults(
            objective,
            selectedNewsIds,
            basicSynthesis,
            analysisMetadata,
            userId
          );
          
          console.log('Correspondances sauvegardées');
        } catch (saveError) {
          console.warn('Erreur sauvegarde correspondances (non bloquant):', saveError);
        }
      }

      const result = {
        enrichment: {
          discovered: enrichResult.discovered || 0,
          added: enrichResult.added || 0
        },
        analysis: {
          relevant: relevantNews.length,
          articles: relevantNews
        },
        success: relevantNews.length > 0,
        message: `${enrichResult.added} nouvelles actualités ajoutées, ${relevantNews.length} analysées comme pertinentes`
      };

      console.log('Découverte + Analyse terminée:', result.message);
      
      return result;
      
    } catch (error) {
      console.error('Erreur découverte et analyse:', error);
      throw error;
    }
  }

  async discoverAndAddNews(searchQuery, userId, options = {}) {
    try {
      if (!userId) {
        throw new Error('Vous devez être connecté pour découvrir des actualités');
      }

      const {
        maxNews = 20,
        language = 'fr',
        country = 'FR'
      } = options;

      console.log(`Découverte pour: "${searchQuery}"`);

      const auth = getAuth();
      let token = null;
      
      try {
        const user = auth.currentUser;
        if (user) {
          token = await user.getIdToken(true);
        } else {
          throw new Error('Utilisateur non authentifié');
        }
      } catch (authError) {
        console.error('Erreur auth:', authError);
        throw new Error('Erreur d\'authentification');
      }

      const userObjective = `Je recherche les dernières actualités récentes et fiables concernant: ${searchQuery}`;

      const searchResult = await sourceService.getRecommendedSources({
        userId: userId,
        userObjective: userObjective,
        query: searchQuery,
        languages: [language],
        country: country,
        maxSources: maxNews,
        selectedProviders: ['Perplexity', 'Google'], 
        newsSearch: true, 
        timeRange: 'recent',
        sortBy: 'recency',
        includeBreaking: true,
        qualityFilter: 'high',
        verifyAccessibility: true,
        enableDomainFallback: true,
        detectParking: true,
        accessibilityMode: 'enhanced'
      }, token);

      if (!searchResult || !searchResult.sources || searchResult.sources.length === 0) {
        return {
          discovered: 0,
          added: 0,
          news: [],
          message: 'Aucune nouvelle actualité trouvée'
        };
      }

      console.log(`${searchResult.sources.length} sources découvertes`);

      const { savedNews, errors } = await this.transformAndSaveSourceToNews(
        searchResult.sources.slice(0, maxNews), 
        userId
      );

      console.log(`${savedNews.length} nouvelles actualités ajoutées`);
      this.clearCache();

      return {
        discovered: searchResult.sources.length,
        added: savedNews.length,
        news: savedNews,
        errors: errors
      };

    } catch (error) {
      console.error(`Erreur découverte:`, error);
      return { 
        discovered: 0, 
        added: 0, 
        news: [], 
        error: error.message
      };
    }
  }

  async transformAndSaveSourceToNews(sources, userId) {
    const savedNews = [];
    const errors = [];

    for (let index = 0; index < sources.length; index++) {
      const source = sources[index];
      const baseTimestamp = Date.now() - (index * 60000);
      
      try {
        const newsData = {
          title: source.title || this.generateTitleFromSource(source),
          content: source.description || source.summary || '',
          url: source.usableUrl || source.recommendedUrl || source.url,
          originalUrl: source.originalUrl || source.url,
          imageUrl: source.imageUrl || this.generateNewsImage(),
          source: source.domain || source.provider || 'Source découverte',
          author: source.author || '',
          provider: source.provider || 'sourceservice',
          publishedAt: source.publishedAt || new Date(baseTimestamp).toISOString(),
          language: source.language || 'fr',
          country: source.country || 'FR',
          relevanceScore: source.relevanceScore || 7,
          qualityScore: source.qualityScore || 6,
          credibilityScore: source.credibilityScore || 7,
          isBreaking: this.isBreakingNews(source),
          isVerified: source.isVerified || true
        };

        const exists = await this.checkNewsExists(newsData.url);
        if (!exists) {
          const realFirebaseId = await this.addNewsToDatabase(newsData, userId);
          
          savedNews.push({
            ...newsData,
            id: realFirebaseId
          });
          
          console.log(`Source transformée et sauvegardée avec ID Firebase: ${realFirebaseId}`);
        } else {
          console.log(`Source existe déjà : ${newsData.title}`);
        }
      } catch (error) {
        console.warn(`Erreur transformation source ${index}:`, error);
        errors.push({ source, error });
      }
    }

    return { savedNews, errors };
  }

  async addNewsToDatabase(newsData, userId) {
    try {
      const cleanNewsData = { ...newsData };
      delete cleanNewsData.id;
      
      const newsToAdd = {
        ...cleanNewsData,
        addedBy: userId,
        addedAt: new Date().toISOString(),
        status: 'active',
        totalViews: 0,
        discoveryMethod: newsData.discoveryMethod || 'sourceservice',
        lastUpdated: new Date().toISOString()
      };

      const docRef = await addDoc(collection(db, 'news'), newsToAdd);
      console.log(`Actualité ajoutée avec ID Firebase: ${docRef.id}`);
      
      return docRef.id;
    } catch (error) {
      console.error('Erreur ajout actualité:', error);
      throw error;
    }
  }

  // ================================
  // MÉTHODES DE RÉCUPÉRATION PAR THÈME
  // ================================

  async getNewsByTheme(theme, maxNews = 25, userId = null) {
    try {
      const cacheKey = `news_theme_${theme}`;
      const cached = this.getFromCache(cacheKey);
      if (cached) {
        console.log(`Cache hit: ${theme}`);
        return cached.map(this.enhanceNewsData);
      }

      console.log(`Chargement thème: ${theme}`);

      const q = query(
        collection(db, 'news'),
        where('theme', '==', theme),
        where('status', '==', 'active'),
        orderBy('publishedAt', 'desc'),
        limit(maxNews)
      );

      const querySnapshot = await getDocs(q);
      const news = querySnapshot.docs.map(doc => ({
        id: doc.id,
        source: 'firebase',
        ...doc.data()
      }));

      console.log(`${news.length} actualités pour ${theme}`);
      
      this.setCache(cacheKey, news);
      return news.map(this.enhanceNewsData);

    } catch (error) {
      console.error(`Erreur thème ${theme}:`, error);
      return [];
    }
  }

  async discoverNewsByTheme(theme, userId, options = {}) {
    try {
      if (!userId) {
        throw new Error('Vous devez être connecté pour découvrir des actualités');
      }

      const {
        maxNews = 10,
        language = 'fr',
        country = 'FR'
      } = options;

      console.log(`Découverte thématique: ${theme}`);

      const themeConfig = this.newsThemes[theme];
      if (!themeConfig) {
        throw new Error(`Thème ${theme} non configuré`);
      }

      const searchQuery = themeConfig.searchTerms;
      const userObjective = `Je recherche les dernières actualités récentes et fiables dans le domaine ${this.getThemeDisplayName(theme)}.`;

      const auth = getAuth();
      let token = null;
      
      try {
        const user = auth.currentUser;
        if (user) {
          token = await user.getIdToken(true);
        } else {
          throw new Error('Utilisateur non authentifié');
        }
      } catch (authError) {
        console.error('Erreur auth:', authError);
        throw new Error('Erreur d\'authentification');
      }

      const searchResult = await sourceService.getRecommendedSources({
        userId: userId,
        userObjective: userObjective,
        query: searchQuery,
        languages: [language],
        country: country,
        maxSources: maxNews,
        selectedProviders: ['Perplexity'], 
        newsSearch: true, 
        timeRange: 'recent',
        categories: themeConfig.categories,
        sortBy: 'recency',
        includeBreaking: true,
        qualityFilter: 'high',
        verifyAccessibility: true,
        enableDomainFallback: true,
        detectParking: true,
        accessibilityMode: 'enhanced'
      }, token);

      if (!searchResult || !searchResult.sources || searchResult.sources.length === 0) {
        return {
          discovered: 0,
          added: 0,
          news: [],
          theme: theme,
          message: 'Aucune nouvelle actualité trouvée'
        };
      }

      console.log(`${searchResult.sources.length} sources découvertes pour ${theme}`);

      const { savedNews, errors } = await this.transformAndSaveSourceToNewsWithTheme(
        searchResult.sources.slice(0, maxNews), 
        theme,
        userId
      );

      console.log(`${savedNews.length} nouvelles actualités avec thème ${theme}`);
      this.clearCache();

      return {
        discovered: searchResult.sources.length,
        added: savedNews.length,
        news: savedNews,
        errors: errors,
        theme: theme
      };

    } catch (error) {
      console.error(`Erreur découverte ${theme}:`, error);
      return { 
        discovered: 0, 
        added: 0, 
        news: [], 
        error: error.message,
        theme: theme 
      };
    }
  }

  async transformAndSaveSourceToNewsWithTheme(sources, theme, userId) {
    const savedNews = [];
    const errors = [];

    for (let index = 0; index < sources.length; index++) {
      const source = sources[index];
      const baseTimestamp = Date.now() - (index * 60000);
      
      try {
        const newsData = {
          title: source.title || this.generateTitleFromSource(source),
          content: source.description || source.summary || '',
          url: source.usableUrl || source.recommendedUrl || source.url,
          originalUrl: source.originalUrl || source.url,
          imageUrl: source.imageUrl || this.generateNewsImage(),
          source: source.domain || source.provider || 'Source découverte',
          author: source.author || '',
          provider: source.provider || 'sourceservice',
          publishedAt: source.publishedAt || new Date(baseTimestamp).toISOString(),
          theme: theme,
          language: source.language || 'fr',
          country: source.country || 'FR',
          relevanceScore: source.relevanceScore || 7,
          qualityScore: source.qualityScore || 6,
          credibilityScore: source.credibilityScore || 7,
          isBreaking: this.isBreakingNews(source),
          isVerified: source.isVerified || true
        };

        const exists = await this.checkNewsExists(newsData.url);
        if (!exists) {
          const realFirebaseId = await this.addNewsToDatabase(newsData, userId);
          
          savedNews.push({
            ...newsData,
            id: realFirebaseId
          });
          
          console.log(`Source transformée avec thème ${theme} et ID Firebase: ${realFirebaseId}`);
        } else {
          console.log(`Source existe déjà : ${newsData.title}`);
        }
      } catch (error) {
        console.warn(`Erreur transformation source ${index}:`, error);
        errors.push({ source, error });
      }
    }

    return { savedNews, errors };
  }

  // ================================
  // MÉTHODES UTILITAIRES
  // ================================

  extractKeywordsFromObjective(objective) {
    const title = objective.title.toLowerCase();
    
    const titleWords = title.split(' ')
      .filter(word => word.length > 3)
      .filter(word => !['dans', 'avec', 'pour', 'vers', 'être', 'avoir', 'faire'].includes(word));
    
    return titleWords;
  }

  async generateBasicSynthesis(articles, objective) {
    try {
      if (!articles || articles.length === 0) return '';

      const topArticles = articles.slice(0, 5);
      
      const newsContext = topArticles.map((article, index) => {
        const scoreInfo = article.semanticScore ? ` [Score: ${article.semanticScore}/10]` : '';
        return `${index + 1}. "${article.title}" - ${article.content?.substring(0, 150) || 'Pas de contenu'}${scoreInfo}`;
      }).join('\n');

      const prompt = `Génère une synthèse concise (max 200 mots) des actualités suivantes pour l'objectif "${objective.title}" :

${newsContext}

Focus sur les insights pratiques et les tendances pertinentes pour cet objectif.`;

      const synthesis = await askGPT(prompt);
      return synthesis || 'Synthèse générée automatiquement lors de la sauvegarde.';
      
    } catch (error) {
      console.warn('Erreur génération synthèse basique:', error);
      return `Analyse de ${articles.length} actualités pertinentes pour l'objectif "${objective.title}" effectuée le ${new Date().toLocaleDateString('fr-FR')}.`;
    }
  }

  async checkNewsExists(url) {
    try {
      const q = query(
        collection(db, 'news'),
        where('url', '==', url),
        where('status', '==', 'active'),
        limit(1)
      );

      const querySnapshot = await getDocs(q);
      return !querySnapshot.empty;
    } catch (error) {
      console.warn('Erreur vérification existence:', error);
      return false;
    }
  }

  generateTitleFromSource(source) {
    if (source.title) return source.title;
    if (source.description) return source.description.substring(0, 100) + '...';
    return 'Actualité découverte';
  }

  generateNewsImage() {
    return '/images/news/general-default.jpg';
  }

  isBreakingNews(source) {
    const now = new Date();
    const publishDate = new Date(source.publishedAt || now);
    const hoursDiff = (now - publishDate) / (1000 * 60 * 60);
    
    return hoursDiff < 6;
  }

  async recordNewsView(newsId, userId = null) {
    try {
      if (!newsId || typeof newsId !== 'string' || newsId.trim() === '') {
        console.warn('ID d\'actualité invalide:', newsId);
        return;
      }

      const cleanNewsId = newsId.trim();
      const newsRef = doc(db, 'news', cleanNewsId);
      
      const updateData = {
        totalViews: increment(1),
        lastViewedAt: new Date().toISOString()
      };

      if (userId && typeof userId === 'string') {
        updateData.lastViewedBy = userId;
      }

      await updateDoc(newsRef, updateData);
      console.log(`Vue enregistrée pour: ${cleanNewsId}`);
      
    } catch (error) {
      console.warn('Erreur enregistrement vue:', error.message);
    }
  }

  enhanceNewsData = (news) => {
    const safeId = news.id || `news_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    return {
      ...news,
      id: safeId,
      title: news.title || 'Actualité',
      content: news.content || '',
      imageUrl: news.imageUrl || this.generateNewsImage(),
      publishedAt: news.publishedAt || new Date().toISOString(),
      timeAgo: this.getTimeAgo(news.publishedAt),
      relevanceScore: news.relevanceScore || 5,
      qualityScore: news.qualityScore || 5,
      source: news.source || 'Source inconnue',
      url: news.url || '#',
      enhancedAt: Date.now()
    };
  };

  getTimeAgo(dateString) {
    if (!dateString) return 'Récemment';
    
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInMilliseconds = now - date;
      const diffInHours = Math.floor(diffInMilliseconds / (1000 * 60 * 60));
      const diffInDays = Math.floor(diffInHours / 24);
      
      if (diffInHours < 1) return 'Il y a moins d\'une heure';
      if (diffInHours < 24) return `Il y a ${diffInHours}h`;
      if (diffInDays < 7) return `Il y a ${diffInDays}j`;
      
      return date.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short'
      });
    } catch (error) {
      console.warn('Erreur parsing date:', dateString);
      return 'Date inconnue';
    }
  }

  getThemeDisplayName(theme) {
    const displayNames = {
      'sports': 'Sports et Fitness',
      'creativity': 'Créativité et Arts',
      'travel': 'Voyages et Tourisme',
      'finance': 'Finance et Économie',
      'entrepreneurship': 'Entrepreneuriat',
      'career': 'Carrière et Emploi',
      'health': 'Santé et Bien-être',
      'education': 'Éducation et Formation',
      'realestate': 'Immobilier',
      'personal': 'Développement Personnel'
    };
    
    return displayNames[theme] || theme;
  }

  // ================================
  // MÉTHODES DE RÉCUPÉRATION GÉNÉRALE
  // ================================

  async getAllNews(maxNews = 150, userId = null) {
    try {
      console.log(`Actualités générales (${maxNews})`);
      
      const cacheKey = 'all_news_general';
      const cached = this.getFromCache(cacheKey);
      if (cached) {
        return cached;
      }

      const q = query(
        collection(db, 'news'),
        where('status', '==', 'active'),
        orderBy('publishedAt', 'desc'),
        limit(maxNews)
      );

      const querySnapshot = await getDocs(q);
      const allNews = querySnapshot.docs.map(doc => ({
        id: doc.id,
        source: 'firebase',
        ...doc.data()
      }));
      
      this.setCache(cacheKey, allNews);
      return allNews.map(this.enhanceNewsData);
      
    } catch (error) {
      console.error('Erreur getAllNews:', error);
      return [];
    }
  }

  async searchNews(searchQuery, theme = null, options = {}) {
    try {
      console.log(`Recherche textuelle: "${searchQuery}" dans ${theme}`);
      
      let q = collection(db, 'news');
      const constraints = [where('status', '==', 'active')];

      if (theme && theme !== 'all') {
        constraints.push(where('theme', '==', theme));
      }

      q = query(q, ...constraints, orderBy('publishedAt', 'desc'), limit(200));

      const querySnapshot = await getDocs(q);
      let news = querySnapshot.docs.map(doc => ({
        id: doc.id,
        source: 'firebase_search',
        ...doc.data()
      }));

      if (searchQuery && searchQuery.trim()) {
        const searchTerms = searchQuery.toLowerCase().split(' ').filter(term => term.length > 2);
        news = news.filter(article => {
          const searchableText = `${article.title} ${article.content}`.toLowerCase();
          return searchTerms.some(term => searchableText.includes(term));
        });
      }

      return news.slice(0, options.maxResults || 30).map(this.enhanceNewsData);

    } catch (error) {
      console.error('Erreur recherche textuelle:', error);
      return [];
    }
  }

  // ================================
  // MÉTHODES DE GESTION ET ADMINISTRATION
  // ================================

  async updateNews(newsId, updates, userId) {
    try {
      if (!userId) {
        throw new Error('Vous devez être connecté pour modifier une actualité');
      }

      const newsRef = doc(db, 'news', newsId);
      await updateDoc(newsRef, {
        ...updates,
        lastUpdated: new Date().toISOString(),
        lastUpdatedBy: userId
      });
      
      console.log(`Actualité mise à jour: ${newsId}`);
      this.clearCache();
      return true;
    } catch (error) {
      console.error('Erreur mise à jour actualité:', error);
      throw error;
    }
  }

  async deleteNews(newsId, userId) {
    try {
      if (!userId) {
        throw new Error('Vous devez être connecté pour supprimer une actualité');
      }

      await deleteDoc(doc(db, 'news', newsId));
      console.log(`Actualité supprimée: ${newsId}`);
      this.clearCache();
      return true;
    } catch (error) {
      console.error('Erreur suppression actualité:', error);
      throw error;
    }
  }

  async updateAllNewsThemes(userId) {
    try {
      console.log('Mise à jour complète des actualités par thème');

      if (!userId) {
        throw new Error('Vous devez être connecté pour mettre à jour les actualités');
      }

      const auth = getAuth();
      try {
        const user = auth.currentUser;
        if (!user) {
          throw new Error('Utilisateur non authentifié');
        }
      } catch (authError) {
        console.error('Erreur authentification:', authError);
        throw new Error('Erreur d\'authentification');
      }

      const themes = Object.keys(this.newsThemes);
      const results = {};

      for (const theme of themes) {
        try {
          const result = await this.discoverNewsByTheme(theme, userId, { maxNews: 15 });
          results[theme] = result;
          
          await new Promise(resolve => setTimeout(resolve, 2000));
        } catch (error) {
          console.error(`Erreur mise à jour thème ${theme}:`, error);
          results[theme] = { discovered: 0, added: 0, error: error.message };
        }
      }

      console.log('Mise à jour complète terminée:', results);
      return results;

    } catch (error) {
      console.error('Erreur mise à jour complète:', error);
      throw error;
    }
  }

  async getNewsStats() {
    try {
      console.log('Calcul des statistiques...');
      const allNews = await this.getAllNews();
      
      const stats = {
        totalNews: allNews.length,
        totalViews: allNews.reduce((sum, news) => sum + (news.totalViews || 0), 0),
        byTheme: {},
        byDiscoveryMethod: {},
        recentlyAdded: 0,
        averageQuality: 0
      };
      
      allNews.forEach(news => {
        const theme = news.theme || 'unknown';
        stats.byTheme[theme] = (stats.byTheme[theme] || 0) + 1;
        
        const method = news.discoveryMethod || 'manual';
        stats.byDiscoveryMethod[method] = (stats.byDiscoveryMethod[method] || 0) + 1;
      });
      
      if (allNews.length > 0) {
        const totalQuality = allNews.reduce((sum, news) => sum + (news.qualityScore || 0), 0);
        stats.averageQuality = Math.round(totalQuality / allNews.length * 10) / 10;
      }
      
      const dayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
      stats.recentlyAdded = allNews.filter(news => 
        news.addedAt && new Date(news.addedAt) > dayAgo
      ).length;
      
      console.log('Statistiques calculées:', stats);
      return stats;
      
    } catch (error) {
      console.error('Erreur statistiques:', error);
      return {
        totalNews: 0,
        totalViews: 0,
        byTheme: {},
        byDiscoveryMethod: {},
        recentlyAdded: 0,
        averageQuality: 0
      };
    }
  }

  // ================================
  // MÉTHODES DE CACHE
  // ================================

  getFromCache(key) {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }
    return null;
  }

  setCache(key, data) {
    this.cache.set(key, {
      data: data,
      timestamp: Date.now()
    });
  }

  clearCache(key = null) {
    if (key) {
      this.cache.delete(key);
      this.semanticCache.delete(key);
    } else {
      this.cache.clear();
      this.semanticCache.clear();
    }
    console.log('Cache nettoyé');
  }

  // ================================
  // MÉTHODES D'INFORMATION ET CONFIGURATION
  // ================================

  getServiceInfo() {
    return {
      cacheSize: this.cache.size,
      semanticCacheSize: this.semanticCache.size,
      cacheTimeout: this.cacheTimeout / 1000 / 60,
      semanticCacheTimeout: this.semanticCacheTimeout / 1000 / 60,
      themesConfigured: Object.keys(this.newsThemes).length,
      availableThemes: Object.keys(this.newsThemes),
      version: '2.0 - Simplifié sans liaison thème-objectif'
    };
  }

  getAvailableThemes() {
    return Object.keys(this.newsThemes).map(themeId => ({
      id: themeId,
      label: this.getThemeDisplayName(themeId),
      categories: this.newsThemes[themeId].categories
    }));
  }

  /**
   * MÉTHODE DÉPRÉCIÉE - Remplacée par getNewsForObjective
   * @deprecated Utilisez getNewsForObjective() à la place
   */
  async searchNewsForObjective(objective, userId = null, options = {}) {
    console.warn('DEPRECATED: searchNewsForObjective est déprécié. Utilisez getNewsForObjective() à la place.');
    
    const result = await this.getNewsForObjective(objective.id, userId, options);
    return result.current || [];
  }
}

// ================================
// EXPORT ET INITIALISATION
// ================================

const newsFirebaseService = new NewsFirebaseService();

console.log('NewsFirebaseService v2.0 initialisé');
console.log('Configuration:', newsFirebaseService.getServiceInfo());

export default newsFirebaseService;
